import HistoryIcon from '@mui/icons-material/History'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useImportLoansByIds } from '@src/api/admin-commands-api'
import { useNotification } from '@src/providers/NotificationProvider'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CommandHistoryDialog from './CommandHistoryDialog'
import CommandRecentExecutionsTable from './CommandRecentExecutionsTable'

interface ParsedLoanIds {
  validIds: string[]
  invalidTokens: string[]
}

const parseLoanIds = (rawInput: string): ParsedLoanIds => {
  const tokens = rawInput
    .split(/[\n,;]/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0)

  const validIds: string[] = []
  const invalidTokens: string[] = []
  const seenIds = new Set<string>()

  tokens.forEach((token) => {
    if (!/^\d+$/.test(token)) {
      invalidTokens.push(token)
      return
    }
    if (!seenIds.has(token)) {
      seenIds.add(token)
      validIds.push(token)
    }
  })

  return { validIds, invalidTokens }
}

const ImportLoansByIdsCard = () => {
  const { t } = useTranslation()
  const { showSuccess, showError } = useNotification()
  const importLoansByIds = useImportLoansByIds()

  const [rawInput, setRawInput] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false)

  const { validIds, invalidTokens } = useMemo(() => parseLoanIds(rawInput), [rawInput])
  const isPending = importLoansByIds.isPending
  const canSubmit = validIds.length > 0 && invalidTokens.length === 0 && !isPending

  const handleOpenDialog = () => setIsDialogOpen(true)
  const handleCloseDialog = () => {
    if (!isPending) setIsDialogOpen(false)
  }

  const handleConfirm = async () => {
    try {
      await importLoansByIds.mutateAsync(validIds)
      showSuccess(t('admin.devTools.messages.success'))
      setRawInput('')
    } catch {
      showError(t('admin.devTools.messages.error'))
    } finally {
      setIsDialogOpen(false)
    }
  }

  return (
    <>
      <Card
        elevation={3}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
        }}
      >
        <CardHeader
          avatar={<WarningAmberIcon color="warning" />}
          title={<Typography variant="h6">{t('admin.devTools.commands.importLoansByIds.title')}</Typography>}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('admin.devTools.commands.importLoansByIds.description')}
            </Typography>
            <TextField
              label={t('admin.devTools.commands.importLoansByIds.inputLabel')}
              placeholder={t('admin.devTools.commands.importLoansByIds.placeholder')}
              value={rawInput}
              onChange={(event) => setRawInput(event.target.value)}
              disabled={isPending}
              multiline
              minRows={4}
              fullWidth
            />
            {invalidTokens.length > 0 && (
              <Alert severity="error">
                {t('admin.devTools.commands.importLoansByIds.invalidIds', { ids: invalidTokens.join(', ') })}
              </Alert>
            )}
            {validIds.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('admin.devTools.commands.importLoansByIds.validCount', { nb: validIds.length })}
              </Typography>
            )}
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <Button
            variant="contained"
            color="warning"
            onClick={handleOpenDialog}
            disabled={!canSubmit}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('admin.devTools.commands.importLoansByIds.actionLabel')}
          </Button>
        </CardActions>

        <Divider />

        <Stack spacing={1} sx={{ px: 2, py: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('admin.devTools.history.recentTitle')}
          </Typography>
          <CommandRecentExecutionsTable commandType={DevToolsCommandType.ImportLoansByIds} />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="text"
              startIcon={<HistoryIcon />}
              onClick={() => setIsHistoryDialogOpen(true)}
            >
              {t('admin.devTools.history.viewFullHistory')}
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.devTools.dialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning">
              {t('admin.devTools.commands.importLoansByIds.confirmationMessage', { nb: validIds.length })}
            </Alert>
            <DialogContentText>{t('admin.devTools.dialog.prompt')}</DialogContentText>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isPending}>
            {t('admin.devTools.dialog.cancel')}
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            color="error"
            variant="contained"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('admin.devTools.dialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <CommandHistoryDialog
        open={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
        commandType={DevToolsCommandType.ImportLoansByIds}
        commandTitle={t('admin.devTools.commands.importLoansByIds.title')}
      />
    </>
  )
}

export default ImportLoansByIdsCard
